// ONB.B2-FIX.D1 — Tech Lead /setup/accept magic-link landing page.
//
// Consumes a magic-link token from the URL (?token=<hex>) and POSTs to
// the existing /setup-invitations/accept endpoint. Mirrors
// AcceptInvitePage.tsx's structure per Q2 — same problem shape (token-
// bearing magic link → backend POST → redirect), same shape solution.
//
// Backend contract (verified at recon time against
// routes/setup-invitations.routes.ts:189-206 and
// services/setupInvitationService.ts:218-261):
//   Success 200 → { setupProjectId, redirectTo } — page navigates to
//                 redirectTo; setupProjectId is not consumed.
//   400       → "Invitation is invalid, expired, or already used"
//                 (collapsed; backend deliberately does not distinguish
//                  expired / used / unknown to prevent enumeration —
//                  see setupInvitationService.ts:248-252).
//   403       → "This invitation was sent to a different email address"
//                 (distinguishable error class; renders separate state
//                  with Sign out CTA).
//   5xx       → generic retry state.
//
// Safeguards (S1-S4 from kickoff):
//   S1: Unauthenticated branch URL-encodes /setup/accept?token=<token> as
//       the ?redirect= param. LoginPage's redirect-honoring (this same
//       PR) carries it back through the auth round trip.
//   S2: redirectTo comes from the parsed response — never hard-coded.
//       Defensive Q5 fallback to /dashboard if backend response lacks it.
//   S3: Every successful-path navigate uses { replace: true } so the
//       browser back button does not return to the consumed magic link.
//   S4: Token is never written to console.log, analytics, or Sentry
//       contexts. Errors thrown by apiFetch carry only the backend's
//       error message (no token interpolation in messages).

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface AcceptResponse {
  setupProjectId: string;
  redirectTo?: string;
  // #1975 (S2) — a session re-scoped to the tenant just joined. The token the
  // user arrives with is tenant-less (the skip path mints it that way at
  // registration, because no membership existed yet), so without swapping this
  // in they would land in the dashboard with no tenant context.
  token?: string;
}

// Success path navigates inline inside the .then callback and unmounts
// this page (the route changes), so there's no 'success' state variant —
// the loading UI continues to render until the navigate effect commits,
// which is the most truthful representation of "request in flight, page
// about to unmount". R2 N1 fix.
type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'invalid-or-used' }
  | { kind: 'email-mismatch' }
  | { kind: 'role-filled' }
  | { kind: 'server-error'; message: string };

export default function SetupAcceptPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const authToken = localStorage.getItem('blueprint_token');
  const { setAuthFromToken } = useAuth();

  const [state, setState] = useState<State>({ kind: 'idle' });

  useEffect(() => {
    if (!token || !authToken) return;

    // Effect deps are [token, authToken, navigate] only — NOT state.kind.
    // Including state.kind would cause the effect to re-run when the
    // initial setState('loading') commits, firing the cleanup
    // (cancelled=true) before the in-flight apiFetch's .then resolves,
    // which would leave the page stuck in 'loading' forever. The
    // `cancelled` flag still handles real unmount correctly.
    //
    // R1 F2 fix: navigate-on-success is folded into the .then callback
    // here, eliminating the previous separate useEffect that depended
    // on the full state object reference. The earlier shape risked
    // re-firing on every state transition; folding the navigate inline
    // removes the second effect entirely and locks the navigate to
    // exactly one site, gated by the cancelled flag.
    let cancelled = false;

    apiFetch<AcceptResponse>('/setup-invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then((data) => {
        if (cancelled) return;
        // #1975 (S2) — adopt the tenant-scoped session BEFORE navigating, so
        // the destination renders with the invitation's tenant already in
        // context rather than briefly with the tenant-less one.
        if (data.token) setAuthFromToken(data.token);
        // Q5 defensive fallback: if backend returns 200 without a valid
        // redirectTo (regression in setupInvitationService, future
        // backend rewrite), degrade to /dashboard rather than calling
        // navigate(undefined). T-D1.13.
        const target =
          data.redirectTo && typeof data.redirectTo === 'string'
            ? data.redirectTo
            : '/dashboard';
        // S3: every successful-path navigate uses { replace: true }.
        // No setState — the loading UI continues to render until this
        // navigate commits and unmounts the page (R2 N1 fix).
        navigate(target, { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        // #2437 — a stale/invalid session token in localStorage passes the
        // `!authToken` presence check above but is correctly rejected by the
        // server (401). Branch on status first — before the message-text
        // fallback below — because authenticate()'s own rejection message is
        // literally "Invalid or expired token" (auth.ts), which the
        // substring match below would otherwise misclassify as the
        // INVITATION being expired rather than the session. Same remedy as
        // the !authToken case (sign in, land back here), so reuse that
        // prompt rather than the wrong "ask your buyer to resend" CTA.
        // Same fix shape as AcceptInvitePage.tsx (#2432-class).
        if (err instanceof ApiError && err.status === 401) {
          setState({ kind: 'unauthenticated' });
          return;
        }
        const msg = err instanceof Error ? err.message : 'Failed to accept invitation';
        // Email-mismatch and role-filled are each the sole producer of their
        // substring (traced against every AppError reachable from
        // acceptSetupInvitation — #2437 recon) so they cannot collide with
        // the 401 case handled above or with each other.
        if (msg.includes('different email')) {
          setState({ kind: 'email-mismatch' });
        } else if (msg.includes('Technical Lead role is filled')) {
          // #1690 — set-once CAS 409: a Tech Lead was already assigned
          // (either the invite-side guard or the accept-side race loser).
          // Checked BEFORE the invalid-or-used branch below: this message
          // is deliberately worded without "invalid"/"expired"/"already" so
          // it can't be mis-collapsed into that state, whose "ask your
          // buyer to send a new one" CTA would be actively wrong here — a
          // new invite doesn't help when the role is already filled.
          setState({ kind: 'role-filled' });
        } else if (msg.includes('invalid') || msg.includes('expired') || msg.includes('already')) {
          // Collapsed state per the backend's deliberate non-enumeration
          // (setupInvitationService.ts:248-252). Same UI for expired,
          // used, and unknown — UI MUST NOT distinguish or we re-open
          // the security oracle the backend closed.
          setState({ kind: 'invalid-or-used' });
        } else {
          setState({ kind: 'server-error', message: msg });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, authToken, navigate, setAuthFromToken]);

  const containerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', padding: 24,
    background: 'var(--bg)', fontFamily: 'var(--font-body)',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 440, width: '100%', padding: 32,
    borderRadius: 'var(--radius-xl)', background: 'var(--surface)',
    border: '1px solid var(--border)', textAlign: 'center',
  };

  const primaryButtonStyle: React.CSSProperties = {
    padding: '10px 24px', borderRadius: 'var(--radius-sm)',
    border: 'none', background: 'var(--primary)', color: '#fff',
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    padding: '10px 24px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
    fontWeight: 500, fontSize: 14, cursor: 'pointer',
  };

  // No token — defensive guard against direct visits without ?token.
  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            Missing invitation token
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            This page was opened without a valid invitation link.
          </p>
          <button onClick={() => navigate('/dashboard', { replace: true })} style={secondaryButtonStyle}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Unauthenticated — redirect to /login carrying the full /setup/accept
  // URL+token as ?redirect=. LoginPage (modified in this PR) reads the
  // param, validates same-origin, and navigates back here after login.
  // S1: token-carrying URL preserved through the auth round trip.
  //
  // R1 F2 fix: encodeURIComponent on the inner token too. Today the
  // backend generates a 64-char hex token (safe), but the backend's
  // token-generation contract isn't enforced client-side; a future
  // change to base64url or other charset could include `+`, `=`, `&`,
  // `%` which would double-decode incorrectly without encoding.
  //
  // #2437 — also reached when the POST fired (a token WAS present in
  // localStorage) but the server rejected it as stale/invalid (401).
  // Same remedy either way; message text distinguishes the two causes.
  if (!authToken || state.kind === 'unauthenticated') {
    const here = `/setup/accept?token=${encodeURIComponent(token)}`;
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            Sign in to accept
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            {state.kind === 'unauthenticated'
              ? 'Your session has expired. Please sign in again to accept this Tech Lead invitation.'
              : 'You need to sign in or create an account to accept this Tech Lead invitation.'}
          </p>
          <button
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(here)}`)}
            style={primaryButtonStyle}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'idle' || state.kind === 'loading') {
    // Renders while the POST is in flight AND through the brief moment
    // between navigate-on-success firing and the route unmounting this
    // page. R2 N1: the previous 'success' render branch was unreachable
    // dead code after F2 folded navigate into the .then callback.
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <p style={{ fontSize: 16, color: 'var(--text)' }}>Accepting invitation…</p>
        </div>
      </div>
    );
  }

  if (state.kind === 'invalid-or-used') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            This invitation is no longer valid
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            It may have expired or already been used. If you still need to accept an invitation, ask your buyer to send a new one.
          </p>
          <button onClick={() => navigate('/dashboard', { replace: true })} style={primaryButtonStyle}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'role-filled') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            Technical Lead already assigned
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            This project already has a Technical Lead — no new invite is needed. Contact your buyer if you believe this is a mistake.
          </p>
          <button onClick={() => navigate('/dashboard', { replace: true })} style={primaryButtonStyle}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'email-mismatch') {
    // R1 F2 fix: encodeURIComponent on the inner token (see F2 comment
    // at the unauthenticated branch above).
    const here = `/setup/accept?token=${encodeURIComponent(token)}`;
    const signOutAndRedirect = () => {
      // Clear the JWT so the user can sign in with the right account.
      // The redirect param carries the token back through the login round
      // trip; LoginPage's open-redirect guard validates same-origin.
      localStorage.removeItem('blueprint_token');
      navigate(`/login?redirect=${encodeURIComponent(here)}`, { replace: true });
    };
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
            Wrong account
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            This invitation was sent to a different email address than the one you are signed in with. Sign out and sign in with the invited account to continue.
          </p>
          <button onClick={signOutAndRedirect} style={primaryButtonStyle}>
            Sign out and try again
          </button>
        </div>
      </div>
    );
  }

  // state.kind === 'server-error'
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--error)', marginBottom: 8 }}>
          Unable to accept invitation
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
          {state.message}
        </p>
        <button onClick={() => navigate('/dashboard', { replace: true })} style={secondaryButtonStyle}>
          Go to dashboard
        </button>
      </div>
    </div>
  );
}
